import React from 'react';
import { BilingualText } from '../components/BilingualText';
import { ArrowLeft, Warehouse, MapPin, Plus, Edit2, Trash2, MoreVertical, Globe, Settings2, CheckCircle2, Clock } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
interface WarehouseData {
  id: string;
  name: string;
  location: string;
  code: string;
  status: 'Active' | 'Inactive';
  timezone: string;
  contact: string;
}
const initialWarehouses: WarehouseData[] = [{
  id: 'WH-001',
  name: 'Riyadh Main DC',
  location: 'Riyadh, KSA',
  code: 'RUH-01',
  status: 'Active',
  timezone: 'GMT+3',
  contact: '+966 11 234 5678'
}, {
  id: 'WH-002',
  name: 'Jeddah Port Hub',
  location: 'Jeddah, KSA',
  code: 'JED-02',
  status: 'Active',
  timezone: 'GMT+3',
  contact: '+966 12 345 6789'
}, {
  id: 'WH-003',
  name: 'Dammam Logistics Center',
  location: 'Dammam, KSA',
  code: 'DMM-03',
  status: 'Active',
  timezone: 'GMT+3',
  contact: '+966 13 456 7890'
}, {
  id: 'WH-004',
  name: 'Dubai South Hub',
  location: 'Dubai, UAE',
  code: 'DXB-04',
  status: 'Inactive',
  timezone: 'GMT+4',
  contact: '+971 4 567 8901'
}];
export function WarehouseSettings() {
  const navigate = useNavigate();
  return <div className="min-h-screen bg-slate-50 pb-24" data-id="element-1362">
      <header className="bg-white px-4 py-6 border-b border-slate-200 flex items-center justify-between sticky top-0 z-10 lg:px-8" data-id="element-1363">
        <div className="flex items-center gap-4" data-id="element-1364">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-1365">
            <ArrowLeft size={24} data-id="element-1366" />
          </button>
          <BilingualText en="Warehouse Settings" ar="إعدادات المستودعات" className="text-xl font-bold" data-id="element-1367" />
        </div>
        <button className="btn-primary py-2 px-4 text-sm" data-id="element-1368">
          <Plus size={18} data-id="element-1369" />
          <span data-id="element-1370">Add Warehouse</span>
        </button>
      </header>

      <main className="p-4 md:p-8 max-w-7xl mx-auto space-y-8" data-id="element-1371">
        {/* Global Settings Section */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1372">
          <div className="lg:col-span-1 space-y-2" data-id="element-1373">
            <BilingualText en="Global Configurations" ar="الإعدادات العامة" className="text-xl" data-id="element-1374" />
            <p className="text-sm text-slate-500" data-id="element-1375">
              Manage system-wide settings that apply to all warehouse locations.
            </p>
          </div>
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1376">
            <div className="card p-4 flex items-center gap-4" data-id="element-1377">
              <div className="p-3 bg-blue-50 text-[#2E3192] rounded-lg" data-id="element-1378">
                <Globe size={20} data-id="element-1379" />
              </div>
              <div data-id="element-1380">
                <div className="font-bold" data-id="element-1381">Default Language</div>
                <div className="text-sm text-slate-500" data-id="element-1382">English / Arabic</div>
              </div>
            </div>
            <div className="card p-4 flex items-center gap-4" data-id="element-1383">
              <div className="p-3 bg-purple-50 text-purple-600 rounded-lg" data-id="element-1384">
                <Clock size={20} data-id="element-1385" />
              </div>
              <div data-id="element-1386">
                <div className="font-bold" data-id="element-1387">Booking Window</div>
                <div className="text-sm text-slate-500" data-id="element-1388">14 Days in Advance</div>
              </div>
            </div>
          </div>
        </section>

        {/* Warehouse List */}
        <section className="space-y-4" data-id="element-1389">
          <BilingualText en="Warehouse Locations" ar="مواقع المستودعات" className="text-xl" data-id="element-1390" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1391">
            {initialWarehouses.map(wh => <div key={wh.id} className="card p-6 space-y-4 hover:border-[#2E3192] transition-all group" data-id="element-1392">
                <div className="flex justify-between items-start" data-id="element-1393">
                  <div className="flex items-center gap-3" data-id="element-1394">
                    <div className={`p-3 rounded-xl ${wh.status === 'Active' ? 'bg-blue-50 text-[#2E3192]' : 'bg-slate-100 text-slate-400'}`} data-id="element-1395">
                      <Warehouse size={24} data-id="element-1396" />
                    </div>
                    <div data-id="element-1397">
                      <div className="font-bold text-lg text-slate-800" data-id="element-1398">
                        {wh.name}
                      </div>
                      <div className="text-sm text-slate-400 font-mono uppercase tracking-wider" data-id="element-1399">
                        {wh.code}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-1400">
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full uppercase ${wh.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-1401">
                      {wh.status}
                    </span>
                    <button className="p-1 text-slate-300 hover:text-slate-600" data-id="element-1402">
                      <MoreVertical size={18} data-id="element-1403" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 py-4 border-y border-slate-50" data-id="element-1404">
                  <div className="space-y-1" data-id="element-1405">
                    <div className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-1406">
                      Location
                    </div>
                    <div className="text-sm flex items-center gap-1.5 text-slate-600" data-id="element-1407">
                      <MapPin size={14} className="text-slate-400" data-id="element-1408" />
                      {wh.location}
                    </div>
                  </div>
                  <div className="space-y-1" data-id="element-1409">
                    <div className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-1410">
                      Timezone
                    </div>
                    <div className="text-sm flex items-center gap-1.5 text-slate-600" data-id="element-1411">
                      <Globe size={14} className="text-slate-400" data-id="element-1412" />
                      {wh.timezone}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2" data-id="element-1413">
                  <div className="text-xs text-slate-400" data-id="element-1414">
                    Contact:{' '}
                    <span className="text-slate-600 font-medium" data-id="element-1415">
                      {wh.contact}
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-1416">
                    <button className="p-2 text-slate-400 hover:text-[#2E3192] hover:bg-blue-50 rounded-lg transition-all" data-id="element-1417">
                      <Edit2 size={16} data-id="element-1418" />
                    </button>
                    <Link to="/settings" className="p-2 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all" data-id="element-1419">
                      <Settings2 size={16} data-id="element-1420" />
                    </Link>
                  </div>
                </div>
              </div>)}
          </div>
        </section>
      </main>
    </div>;
}